<template>
  <div class="card">
    <div class="card-header">
      <div class="card-title">
        <i class="fa-solid fa-calendar"></i>
        <h2>Upcoming Matches</h2>
      </div>
      <button class="btn-text" @click="$emit('view-all')">
        View All
        <i class="fa-solid fa-arrow-right"></i>
      </button>
    </div>
    <div class="card-body">
      <div v-if="matches.length > 0">
        <transition-group name="booking-list">
          <div 
            v-for="match in matches.slice(0, 3)" 
            :key="match.id"
            class="match-item booking-card"
            :class="match.status"
          >
            <div class="booking-header">
              <div class="booking-id">
                <i class="fa-solid fa-futbol"></i>
                <span>Match #{{ match.id }}</span>
              </div>
              <div class="booking-meta">
                <span class="booking-date">
                  <i class="fa-solid fa-clock"></i>
                  {{ formatDate(match.date) }}
                </span>
                <span class="booking-status" :class="match.status">
                  {{ formatMatchStatus(match.status) }}
                </span>
              </div>
            </div>
            <div class="booking-body">
              <div class="booking-details">
                <div class="time-details">
                  <div class="time-slot">
                    <i class="fa-solid fa-calendar-day"></i>
                    {{ formatTime(match.date) }}
                  </div>
                  <div class="duration">
                    <i class="fa-solid fa-hourglass-half"></i>
                    {{ formatDuration(match.duration || match.duration_minutes) }}
                  </div>
                </div>
                <div class="booking-amount">
                  <div class="amount-label">Opponent</div>
                  <div class="amount-value">{{ match.opponent_name || 'TBD' }}</div>
                </div>
              </div>
              <div v-if="match.venue" class="booking-notes">
                <i class="fa-solid fa-map-marker-alt"></i>
                <div class="venue-info">
                  <span class="venue-text">{{ match.venue.name }}{{ match.venue.city ? ', ' + match.venue.city : '' }}</span>
                  <a 
                    v-if="match.venue.notes" 
                    :href="match.venue.notes" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    class="venue-location-link"
                    @click.stop
                  >
                    <i class="fa-solid fa-map-location-dot"></i>
                    View Location
                  </a>
                </div>
              </div>
            </div>
            <div class="booking-actions">
              <button class="btn-action btn-secondary" @click.stop="viewMatchDetails(match)">
                <i class="fa-solid fa-eye"></i>
                Details
              </button>
              <button 
                v-if="['confirmed', 'scheduled'].includes((match.status || '').toLowerCase())" 
                class="btn-action btn-primary" 
                :disabled="checkingTactics === (match.id || match.match_id)"
                @click.stop="handleTacticsClick(match)"
              >
                <i class="fa-solid fa-chess-board"></i>
                <span v-if="checkingTactics === (match.id || match.match_id)">Checking…</span>
                <span v-else>Tactics and Formations</span>
              </button>
            </div>
          </div>
        </transition-group>
      </div>
      <div v-else class="empty-state">
        <div class="empty-icon">
          <i class="fa-solid fa-calendar-times"></i>
        </div>
        <h3>No Upcoming Matches</h3>
        <p>Schedule your first match to get started</p>
        <button class="btn-refresh" @click="$emit('organize-match')">
          <i class="fa-solid fa-plus"></i>
          Organize Match
        </button>
      </div>
    </div>

    <!-- Error modal: insufficient players for this match type -->
    <Teleport to="body">
      <div v-if="showInsufficientModal" class="modal-overlay" @click.self="closeInsufficientModal">
        <div class="modal-card">
          <div class="modal-header">
            <h3>Not enough players</h3>
            <button type="button" class="modal-close" aria-label="Close" @click="closeInsufficientModal">
              &times;
            </button>
          </div>
          <div class="modal-body">
            <p>
              Your team does not have enough players for this match type
              <strong>({{ insufficientPayload.matchTypeLabel }})</strong>.
            </p>
            <p class="player-count">
              You have <strong>{{ insufficientPayload.currentCount }}</strong> player(s).
              Minimum required: <strong>{{ insufficientPayload.minimumRequired }}</strong>.
            </p>
            <p class="modal-cta">
              Please add more players or invite more players before you can plan tactics and formation.
            </p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn-modal-secondary" @click="closeInsufficientModal">Close</button>
            <router-link
              v-if="inviteRoutePath"
              :to="inviteRoutePath"
              class="btn-modal-primary"
              @click="closeInsufficientModal"
            >
              Invite players
            </router-link>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { buildApiUrl } from "@/config/api";

/** Map match_format from API (e.g. 11v11, 7v7) to query value for /my-team/players */
function matchFormatToQuery(matchFormat) {
  if (!matchFormat) return '11-aside';
  const normalized = String(matchFormat).toLowerCase().replace(/\s/g, '');
  if (normalized.includes('11') || normalized === '11v11') return '11-aside';
  if (normalized.includes('7') || normalized === '7v7') return '7-aside';
  if (normalized.includes('6') || normalized === '6v6') return '6-aside';
  return '11-aside';
}

/** Human-readable label for match type */
function matchTypeLabel(matchType) {
  const labels = { '11-aside': '11-a-side', '7-aside': '7-a-side', '6-aside': '6-a-side' };
  return labels[matchType] || matchType;
}

export default {
  name: 'UpcomingMatches',
  props: {
    matches: {
      type: Array,
      default: () => []
    },
    teamId: {
      type: [String, Number],
      default: null
    },
    formatDate: {
      type: Function,
      required: true
    },
    formatTime: {
      type: Function,
      required: true
    },
    formatMatchStatus: {
      type: Function,
      required: true
    }
  },
  emits: ['view-all', 'view-match', 'view-details', 'organize-match', 'view-tactics'],
  data() {
    return {
      checkingTactics: null,
      showInsufficientModal: false,
      insufficientPayload: {
        matchTypeLabel: '',
        currentCount: 0,
        minimumRequired: 0
      }
    };
  },
  computed: {
    inviteRoutePath() {
      if (!this.teamId) return null;
      return `/ipromiseafrica/matchup/football/dashboard/invite-players/${this.teamId}`;
    }
  },
  methods: {
    formatDuration(duration) {
      if (!duration) {
        return '90 minutes';
      }
      
      // If it's already a string with "mins" or "minutes", return as is
      if (typeof duration === 'string' && (duration.includes('mins') || duration.includes('minutes'))) {
        return duration;
      }
      
      // If it's a number (integer), add " minutes"
      const numDuration = parseInt(duration, 10);
      if (!isNaN(numDuration)) {
        return `${numDuration} minutes`;
      }
      
      // Fallback to original value or default
      return duration || '90 minutes';
    },
    viewMatchDetails(match) {
      const matchId = match.id || match.match_id;
      if (this.teamId) {
        this.$router.push({
          name: 'MatchDetails',
          params: { match_id: matchId },
          query: { team_id: this.teamId }
        });
      } else {
        // Fallback: emit event if teamId is not available
        this.$emit('view-details', matchId);
      }
    },
    async handleTacticsClick(match) {
      const matchId = match.id || match.match_id;
      this.checkingTactics = matchId;
      try {
        const matchType = matchFormatToQuery(match.match_format);
        const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
        if (!token) {
          this.$emit('view-tactics', matchId);
          return;
        }
        const url = buildApiUrl(`/api/teams/my-team/players?match_type=${encodeURIComponent(matchType)}`);
        const res = await fetch(url, {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json().catch(() => ({}));
        if (data.meets_minimum === false) {
          this.insufficientPayload = {
            matchTypeLabel: matchTypeLabel(matchType),
            currentCount: data.total ?? 0,
            minimumRequired: data.minimum_required ?? 12
          };
          this.showInsufficientModal = true;
          return;
        }
        this.$emit('view-tactics', matchId);
      } catch (err) {
        console.error('Tactics player check failed:', err);
        this.$emit('view-tactics', matchId);
      } finally {
        this.checkingTactics = null;
      }
    },
    closeInsufficientModal() {
      this.showInsufficientModal = false;
    }
  }
};
</script>

<style scoped>
.card {
  background: var(--card-bg);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px 30px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.card-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-title i {
  font-size: 1.5rem;
  color: var(--section-icon);
}

.card-title h2 {
  margin: 0;
  color: var(--heading-color);
  font-size: 1.5rem;
}

.btn-text {
  background: none;
  border: none;
  color: var(--section-icon);
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.btn-text:hover {
  color: var(--heading-color);
  transform: translateX(5px);
}

.card-body {
  padding: 30px;
}

.booking-card {
  background: var(--card-bg);
  border-radius: 16px;
  padding: 20px;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 16px;
}

.booking-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  border-color: var(--card-hover-border);
}

.booking-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: currentColor;
  opacity: 0.3;
}

.booking-card.pending::before { color: var(--pending-border); }
.booking-card.confirmed::before { color: var(--approved-border); }
.booking-card.completed::before { color: #4299e1; }
.booking-card.cancelled::before { color: var(--rejected-border); }

.booking-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.booking-id {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: var(--heading-color);
}

.booking-meta {
  display: flex;
  align-items: center;
  gap: 15px;
}

.booking-date {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.85rem;
  color: var(--subtext-color);
}

.booking-status {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.booking-status.pending { 
  background: var(--pending-bg); 
  color: var(--pending-color);
  border: 1px solid var(--pending-border);
}
.booking-status.confirmed { 
  background: var(--approved-bg); 
  color: var(--approved-color);
  border: 1px solid var(--approved-border);
}
.booking-status.completed { 
  background: var(--action-success-bg); 
  color: var(--action-success-color);
  border: 1px solid var(--action-success-color);
}
.booking-status.cancelled { 
  background: var(--rejected-bg); 
  color: var(--rejected-color);
  border: 1px solid var(--rejected-border);
}

.booking-body {
  margin-bottom: 15px;
}

.booking-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.time-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.time-slot, .duration {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: var(--heading-color);
}

.booking-amount {
  text-align: right;
}

.amount-label {
  font-size: 0.75rem;
  color: var(--subtext-color);
  margin-bottom: 2px;
}

.amount-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--heading-color);
}

.booking-notes {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  margin-bottom: 15px;
}

.booking-notes i {
  color: var(--subtext-color);
  margin-top: 2px;
  flex-shrink: 0;
}

.venue-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

.venue-text {
  font-size: 0.85rem;
  color: var(--subtext-color);
  line-height: 1.4;
}

.venue-location-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--action-primary-color);
  text-decoration: none;
  font-weight: 600;
  font-size: 0.8rem;
  padding: 6px 12px;
  background: rgba(58, 134, 255, 0.1);
  border: 1px solid rgba(58, 134, 255, 0.3);
  border-radius: 6px;
  transition: all 0.3s ease;
  align-self: flex-start;
}

.venue-location-link:hover {
  background: rgba(58, 134, 255, 0.2);
  border-color: var(--action-primary-color);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(58, 134, 255, 0.3);
}

.venue-location-link i {
  font-size: 0.75rem;
  margin-top: 0;
}

.booking-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.btn-action {
  flex: 1;
  padding: 8px 12px;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.3s ease;
  min-width: 100px;
}

.btn-action:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.1);
  color: var(--subtext-color);
  border: 1px solid var(--border-color-hover);
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.2);
  color: var(--heading-color);
}

.btn-primary {
  background: var(--action-primary-bg);
  color: var(--action-primary-color);
  border: 1px solid var(--action-primary-color);
}

.btn-primary:hover {
  background: var(--action-primary-bg);
  opacity: 0.9;
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  width: 100px;
  height: 100px;
  margin: 0 auto 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  color: var(--subtext-color);
}

.empty-state h3 {
  color: var(--heading-color);
  margin: 0 0 10px 0;
}

.empty-state p {
  color: var(--subtext-color);
  margin-bottom: 20px;
}

.btn-refresh {
  background: var(--edit-gradient);
  color: var(--button-color);
  border: none;
  padding: 10px 20px;
  border-radius: 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-refresh:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px var(--edit-shadow);
}

.booking-list-enter-active,
.booking-list-leave-active {
  transition: all 0.4s ease;
}

.booking-list-enter-from {
  opacity: 0;
  transform: scale(0.9) translateY(20px);
}

.booking-list-leave-to {
  opacity: 0;
  transform: scale(0.9) translateY(-20px);
}

.booking-list-move {
  transition: transform 0.4s ease;
}

/* Insufficient players modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}
.modal-card {
  background: var(--card-bg);
  border-radius: 20px;
  max-width: 440px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}
.modal-header h3 {
  margin: 0;
  color: var(--heading-color);
  font-size: 1.25rem;
}
.modal-close {
  background: none;
  border: none;
  color: var(--subtext-color);
  font-size: 1.5rem;
  cursor: pointer;
  line-height: 1;
  padding: 0 4px;
}
.modal-close:hover {
  color: var(--heading-color);
}
.modal-body {
  padding: 24px;
}
.modal-body p {
  margin: 0 0 12px 0;
  color: var(--subtext-color);
  font-size: 0.95rem;
  line-height: 1.5;
}
.modal-body p:last-of-type {
  margin-bottom: 0;
}
.modal-body .player-count {
  color: var(--heading-color);
}
.modal-body .modal-cta {
  margin-top: 16px;
  color: var(--heading-color);
}
.modal-footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}
.btn-modal-secondary {
  padding: 10px 20px;
  border-radius: 8px;
  border: 1px solid var(--border-color-hover);
  background: rgba(255, 255, 255, 0.1);
  color: var(--subtext-color);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}
.btn-modal-secondary:hover {
  background: rgba(255, 255, 255, 0.15);
  color: var(--heading-color);
}
.btn-modal-primary {
  padding: 10px 20px;
  border-radius: 8px;
  background: var(--action-primary-bg);
  color: var(--action-primary-color);
  border: 1px solid var(--action-primary-color);
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
}
.btn-modal-primary:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}
</style>
